import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { StatusBadgeComponent } from '../status-badge/status-badge.component';

export interface TableColumn {
  key: string;
  label: string;
  type?: 'text' | 'badge' | 'date' | 'actions' | 'link';
  sortable?: boolean;
}

@Component({
  selector: 'app-data-table',
  standalone: true,
  imports: [CommonModule, FormsModule, StatusBadgeComponent],
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss']
})
export class DataTableComponent implements OnInit, OnChanges {
  protected readonly Math = Math;
  @Input() data: any[] = [];
  @Input() columns: TableColumn[] = [];
  @Input() title: string = '';
  @Input() loading: boolean = false;
  @Input() showSearch: boolean = true;
  @Input() pageSize: number = 10;

  @Output() action = new EventEmitter<{ type: string, item: any }>();

  filteredData: any[] = [];
  paginatedData: any[] = [];
  searchQuery: string = '';
  currentPage: number = 1;
  totalPages: number = 1;

  ngOnInit(): void {
    this.processData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] || changes['pageSize']) {
      this.processData();
    }
  }

  processData(): void {
    if (!this.data) return;
    
    this.filteredData = this.data.filter(item => {
      if (!this.searchQuery) return true;
      const search = this.searchQuery.toLowerCase();
      return Object.values(item).some(val => 
        String(val).toLowerCase().includes(search)
      );
    });

    this.totalPages = Math.ceil(this.filteredData.length / this.pageSize);
    this.currentPage = 1;
    this.updatePagination();
  }

  updatePagination(): void {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.paginatedData = this.filteredData.slice(start, end);
  }

  setPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.updatePagination();
  }

  onSearch(): void {
    this.processData();
  }

  exportExcel(): void {
    this.action.emit({ type: 'export', item: this.filteredData });
  }

  onAction(type: string, item: any): void {
    this.action.emit({ type, item });
  }

  /** Robust date formatter — handles Date / Firestore Timestamp / number / string. */
  formatDate(val: any): string {
    if (val == null || val === '') return '—';
    let d: Date | null = null;
    if (val instanceof Date) d = val;
    else if (typeof val?.toDate === 'function') d = val.toDate();
    else if (typeof val?.seconds === 'number') d = new Date(val.seconds * 1000);
    else if (typeof val === 'number') d = new Date(val);
    else if (typeof val === 'string') {
      const t = Date.parse(val);
      if (!isNaN(t)) d = new Date(t);
    }
    if (!d || isNaN(d.getTime())) return '—';
    const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${date}, ${time}`;
  }

  getDepartmentDisplay(departmentIds: string[]): string {
    if (!departmentIds || departmentIds.length === 0) return '—';
    return departmentIds.join(', ');
  }

  getStatusClass(item: any): string {
    const status = this.getUserStatus(item);
    switch (status) {
      case 'active': return 'status-active';
      case 'inactive': return 'status-inactive';
      case 'suspended': return 'status-suspended';
      default: return 'status-unknown';
    }
  }

  getUserStatus(item: any): string {
    if (item.isActive === false) return 'inactive';
    if (item.isActive === true) return 'active';
    return 'unknown';
  }
}
